import {
  ActionConfirmationModal,
  Badge,
  Button,
  CountrySelectFormField,
  Label,
  PlacesAutocompleteFormField,
  SimpleSelectFormField,
  StateSelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { useDeleteContactAddress } from "@/lib/data/react-query";
import { useState } from "react";
import {
  useFormContext,
  useFieldArray,
  useWatch,
  type UseFormReturn,
} from "react-hook-form";
import { type z } from "zod";
import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { AddOrEditContactAddressSlideover } from "@/components/features/contact/ContactAddressesSection/AddOrEditContactAddressSlideover";
import { ContactPrimaryStarButton } from "@/components/features/contact/ContactPrimaryStarButton/ContactPrimaryStarButton";
import { type ContactAddress } from "@/lib/types";
import pluralize from "pluralize";
import { ContactAddressIcon } from "@/lib/utils/iconUtils";
import { formatAddress } from "@/lib/utils/format";
import { ExpandableText } from "@/components/ui/ExpandableText/ExpandableText";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { CheckCircle2, Pencil, Plus, Trash2 } from "lucide-react";
import { CONTACT_ADDRESS_LABELS } from "@/lib/data/static/contactStatics";

const newClientRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function ContactAddressCountryStateRow({
  index,
  disabled,
}: {
  index: number;
  disabled: boolean;
}) {
  const { register, control } =
    useFormContext<z.infer<typeof createContactFormSchema>>();
  const country = useWatch({
    control,
    name: `addresses.${index}.country`,
  });
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className={disabled ? "pointer-events-none opacity-60" : undefined}>
        <CountrySelectFormField
          label="Country"
          register={register(`addresses.${index}.country`)}
          name={`addresses.${index}.country`}
          stateSelectName={`addresses.${index}.state`}
        />
      </div>
      <div className={disabled ? "pointer-events-none opacity-60" : undefined}>
        <StateSelectFormField
          label="State"
          register={register(`addresses.${index}.state`)}
          name={`addresses.${index}.state`}
          country={typeof country === "string" ? country : ""}
        />
      </div>
    </div>
  );
}

function ContactAddressInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  collapseMultiple,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  collapseMultiple?: boolean;
}) {
  const { control, register, getValues, setValue } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses",
  });
  const watchedAddresses = useWatch({ control, name: "addresses" });
  const [expandedAddressId, setExpandedAddressId] = useState<string | null>(
    null,
  );

  const setPrimary = (index: number) => {
    const n = (getValues("addresses") ?? []).length;
    for (let i = 0; i < n; i++) {
      setValue(`addresses.${i}.isPrimary`, i === index, {
        shouldDirty: true,
      });
    }
  };

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    const wasPrimary = getValues(`addresses.${index}.isPrimary`);
    if (getValues(`addresses.${index}.id`) === expandedAddressId) {
      setExpandedAddressId(null);
    }
    remove(index);
    if (wasPrimary) {
      const next = getValues("addresses") ?? [];
      if (next.length > 0) {
        for (let i = 0; i < next.length; i++) {
          setValue(`addresses.${i}.isPrimary`, i === 0, {
            shouldDirty: true,
          });
        }
      }
    }
  };

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }
    const list = getValues("addresses") ?? [];
    const id = newClientRowId();
    append({
      id,
      street1: "",
      street2: "",
      city: "",
      state: "",
      county: null,
      zipCode: "",
      country: "United States",
      label: "",
      description: "",
      isPrimary: list.length === 0,
      dateModified: "",
    });
    setExpandedAddressId(id);
  };

  const addressCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;

  return (
    <div>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <ContactAddressIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Address", addressCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full">
        {fields.length > 0 && (
          <ul className="space-y-3">
            {fields.map((field, index) => {
              const row = watchedAddresses?.[index];
              const isPrimary = !!row?.isPrimary;
              const isExpanded =
                !collapseMultiple || row?.id === expandedAddressId;
              const formattedAddress =
                formatAddress({
                  street1: row?.street1 || undefined,
                  street2: row?.street2 || undefined,
                  city: row?.city || undefined,
                  state: row?.state || "",
                  zipCode: row?.zipCode || undefined,
                  country: row?.country || undefined,
                }) || "New address";
              const label = row?.label?.trim();
              return (
                <li
                  key={field.id}
                  className="space-y-2"
                  data-testid={
                    row?.id ? `contact-address-item-${row.id}` : undefined
                  }
                >
                  {!isExpanded ? (
                    <div className="flex min-w-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        className="-ml-2 h-10 min-w-0 flex-1 justify-start px-2 text-left"
                        onClick={() => {
                          setExpandedAddressId(row?.id ?? null);
                        }}
                        aria-label={`Edit ${formattedAddress}`}
                      >
                        <span className="truncate">{formattedAddress}</span>
                        {label && (
                          <Badge variant="secondary" className="ml-2 h-6">
                            {label}
                          </Badge>
                        )}
                      </Button>
                      <ContactPrimaryStarButton
                        isPrimary={isPrimary}
                        disabled={disabled}
                        onClick={() => {
                          setPrimary(index);
                        }}
                        className="h-10 w-10"
                        ariaLabelCurrent="Primary address"
                        ariaLabelSet="Set as primary address"
                      />
                      {hasDeletePermission && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          disabled={disabled}
                          onClick={() => {
                            handleRemove(index);
                          }}
                          aria-label="Remove address"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex min-w-0 flex-wrap items-stretch gap-2 sm:flex-nowrap sm:gap-3">
                        <div className="flex min-w-0 flex-1 flex-col justify-center">
                          <PlacesAutocompleteFormField
                            register={register(`addresses.${index}.street1`)}
                            defaultValue={row?.street1}
                            label="Address 1"
                            disabled={disabled}
                            onAddressSelect={(address) => {
                              setValue(
                                `addresses.${index}.street1`,
                                address.streetAddress,
                                { shouldValidate: true, shouldDirty: true },
                              );
                              setValue(
                                `addresses.${index}.city`,
                                address.city,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                },
                              );
                              setValue(
                                `addresses.${index}.country`,
                                address.country,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                },
                              );
                              setValue(
                                `addresses.${index}.zipCode`,
                                address.zipCode,
                                { shouldValidate: true, shouldDirty: true },
                              );
                              setValue(
                                `addresses.${index}.state`,
                                address.state,
                                { shouldValidate: true, shouldDirty: true },
                              );
                              setValue(`addresses.${index}.street2`, "", {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }}
                          />
                        </div>
                        <div className="flex w-32 min-w-0 flex-col justify-center self-stretch sm:w-36">
                          <SimpleSelectFormField
                            label="Label"
                            className="h-10 w-full min-w-0"
                            formItemClassName="!m-0 w-full min-w-0"
                            placeholder="Label"
                            register={register(`addresses.${index}.label`)}
                            items={CONTACT_ADDRESS_LABELS as readonly string[]}
                            disabled={disabled}
                          />
                        </div>
                        <div className="flex shrink-0 flex-col justify-center self-stretch">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <ContactPrimaryStarButton
                              isPrimary={isPrimary}
                              disabled={disabled}
                              onClick={() => {
                                setPrimary(index);
                              }}
                              className="h-10 w-10"
                              ariaLabelCurrent="Primary address"
                              ariaLabelSet="Set as primary address"
                            />
                            {hasDeletePermission && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 shrink-0"
                                disabled={disabled}
                                onClick={() => {
                                  handleRemove(index);
                                }}
                                aria-label="Remove address"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <TextFormField
                          label="Address 2"
                          register={register(`addresses.${index}.street2`)}
                          disabled={disabled}
                        />
                        <TextFormField
                          label="City"
                          register={register(`addresses.${index}.city`)}
                          disabled={disabled}
                        />
                        <TextFormField
                          label="Zip"
                          register={register(`addresses.${index}.zipCode`)}
                          disabled={disabled}
                        />
                      </div>
                      <ContactAddressCountryStateRow
                        index={index}
                        disabled={disabled}
                      />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <Button
            type="button"
            variant="link"
            className="contact-subitem-add text-muted-foreground mt-1 h-auto gap-1 px-0"
            onClick={addRow}
            data-testid="add-address-button"
          >
            <Plus className="h-4 w-4" />
            Add address
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ContactAddressesSection({
  addresses,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  collapseMultiple,
  parentForm,
}: {
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  addresses?: ContactAddress[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  collapseMultiple?: boolean;
}) {
  const deleteContactAddress = useDeleteContactAddress();
  const [activeAddressId, setActiveAddressId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (parentForm) {
    return (
      <ContactAddressInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
        collapseMultiple={collapseMultiple}
      />
    );
  }

  const addressList = addresses ?? [];
  const addressCount = Math.max(addressList.length, 1);
  const showEmptyState = addressList.length === 0 && !isAdding;

  const activeAddress = (addresses ?? []).find(
    (address) => address.id === activeAddressId,
  );

  const handleDelete = (address: Pick<ContactAddress, "id">) => {
    deleteContactAddress.mutate({
      id: address.id,
      contactId: contactId,
    });
    setActiveAddressId(null);
  };

  return (
    <div>
      {!hideLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactAddressIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Address", addressCount)}
            </Label>
          </div>
          {hasCreatePermission && (
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-email-button"
            >
              Create address
            </Button>
          )}
        </div>
      )}
      <div className="col-span-full">
        {hideLabel && hasCreatePermission && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-email-button"
            >
              Create address
            </Button>
          </div>
        )}
        {isAdding && (
          <div className="border-border bg-card mt-3 w-full rounded-md border p-3">
            <AddOrEditContactAddressSlideover
              editing={false}
              setOpen={setIsAdding}
              open={isAdding}
              contactId={contactId}
              hasEditPermission={hasEditPermission}
              parentForm={parentForm}
              variant="inline"
            />
          </div>
        )}
        {showEmptyState ? (
          <Typography
            variant="subHeading"
            className="text-muted-foreground mt-3"
          >
            No addresses found.
          </Typography>
        ) : (
          <ul className="mt-3 space-y-3">
            {addressList.map((address, index) => {
              const formattedAddress =
                formatAddress({
                  street1: address.street1 ?? undefined,
                  street2: address.street2 ?? undefined,
                  city: address.city ?? undefined,
                  state: address.state ?? "",
                  zipCode: address.zipCode ?? undefined,
                  country: address.country ?? undefined,
                }) || "-";
              const isPrimary =
                "isPrimary" in address ? address.isPrimary : undefined;
              const label = address.label?.trim();
              const description = address.description?.trim();
              const isActiveEdit = isEditing && address.id === activeAddressId;
              if (isActiveEdit) {
                return (
                  <li
                    key={address.id || `${address.street1}-${index}`}
                    className="border-border bg-card w-full rounded-md border p-3"
                  >
                    <AddOrEditContactAddressSlideover
                      editing={true}
                      activeContactAddress={address}
                      setOpen={(nextOpen) => {
                        setIsEditing(nextOpen);
                        if (!nextOpen) {
                          setActiveAddressId(null);
                        }
                      }}
                      open={isEditing}
                      contactId={contactId}
                      hasEditPermission={hasEditPermission}
                      hasDeletePermission={hasDeletePermission}
                      onDelete={() => {
                        setIsDeleting(true);
                      }}
                      parentForm={parentForm}
                      variant="inline"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={address.id || `${address.street1}-${index}`}
                  className="border-border bg-card w-full rounded-md border p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Typography weight="medium" className="truncate">
                        {formattedAddress}
                      </Typography>
                      {label && (
                        <Badge variant="secondary" className="h-6 shrink-0">
                          {label}
                        </Badge>
                      )}
                      {Boolean(isPrimary) && (
                        <TooltipWrapper message="Primary">
                          <span className="flex items-center">
                            <CheckCircle2
                              className="h-4 w-4 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Primary</span>
                          </span>
                        </TooltipWrapper>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!address.id) {
                          return;
                        }
                        setIsEditing(true);
                        setActiveAddressId(address.id);
                      }}
                      disabled={!address.id}
                      aria-label="Edit address"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  {description && (
                    <div className="mt-2">
                      <ExpandableText
                        text={description}
                        maxLines={3}
                        size="sm"
                        className="text-muted-foreground"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this address?`}
        open={isDeleting && !!activeAddress}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeAddress) {
            handleDelete(activeAddress);
          }
        }}
      />
    </div>
  );
}
